const totalGridChargedBar = '#00477e'
const totalGridChargedBarLightestShade = '#33c6ea'

const solarChargedBar = '#ff8f33'
const solarChargedBarLightestShade = '#ffdb33'

/**
 * Utility function to calculate wallbox shades seen in charging session barchart.
 *
 * Given start and end color, total count of shades that we need to generate between start
 * and end color and index of shade between start and end, it returns the hex code of the
 * calculated shade.
 *
 * It takes total shade count(number of wallboxes) into account so that we split the color range
 * evenly between darkest allowed shade and lightest allowed shade. In case of many wallboxes
 * contained inside a single date, it will reliably calculate many(infinite) shades, although
 * they will become more difficult to discern.
 */
export const getEMobilityChartShade = (props: { palette: 'grid' | 'solar'; shadeIndex: number; shadeCount: number }): string => {
  const { palette, shadeCount, shadeIndex } = props

  const stepSize: number = 1 / shadeCount
  const interpolationFactor = stepSize * shadeIndex

  switch (palette) {
    case 'grid':
      return blendColors(totalGridChargedBar, totalGridChargedBarLightestShade, interpolationFactor)
    case 'solar':
      return blendColors(solarChargedBar, solarChargedBarLightestShade, interpolationFactor)
  }
}

type RGBColor = {
  r: number
  g: number
  b: number
}

/**
 * Given start and end color, calculates any shade between the two. Useful to distinguish
 * different bars inside a vertically stacked barchart.
 */
function blendColors(startHex: string, endHex: string, blendPercentage: number): string {
  /** Ensure the percentage is within the valid range [0, 1] */
  blendPercentage = Math.max(0, Math.min(1, blendPercentage))

  /** Convert hex codes to RGB values */
  const startColor: RGBColor = hexToRgb(startHex)
  const endColor: RGBColor = hexToRgb(endHex)

  /** Interpolate each channel separately */
  const blendedColor: RGBColor = {
    r: startColor.r + (endColor.r - startColor.r) * blendPercentage,
    g: startColor.g + (endColor.g - startColor.g) * blendPercentage,
    b: startColor.b + (endColor.b - startColor.b) * blendPercentage,
  }

  /** Ensure the blended color channels are within the valid range [0, 255] */
  blendedColor.r = Math.max(0, Math.min(255, blendedColor.r))
  blendedColor.g = Math.max(0, Math.min(255, blendedColor.g))
  blendedColor.b = Math.max(0, Math.min(255, blendedColor.b))

  /** Convert RGB values to a hexadecimal string */
  const hexString = `#${Math.round(blendedColor.r).toString(16).padStart(2, '0')}${Math.round(blendedColor.g)
    .toString(16)
    .padStart(2, '0')}${Math.round(blendedColor.b).toString(16).padStart(2, '0')}`

  return hexString
}

/**
 * Converts hex value such as "#aabbcc" to its RGB equivalent "rgb(170, 187, 204)".
 * RGB representation is useful for color transformation such as blending, offsetting,
 * filtering etc.
 */
function hexToRgb(hex: string): RGBColor {
  /** Remove the hash character, if present in #aabbcc */
  hex = hex.replace(/^#/, '')

  /** Convert each of two-character groups from integer written in base 16 system(hex) to regular integer */
  const r = parseInt(hex.slice(0, 2), 16) /** "aa" => 170 */
  const g = parseInt(hex.slice(2, 4), 16) /** "bb" => 187 */
  const b = parseInt(hex.slice(4, 6), 16) /** "cc" => 204 */

  return { r, g, b }
}
